import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const VideoContext = createContext();

export const useVideo = () => {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideo must be used within a VideoProvider');
  }
  return context;
};

export const VideoProvider = ({ children }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [volume, setVolume] = useState(0.5); // Volume par défaut à 50%
  const videoRef = useRef(null);

  // S'assurer que l'état est correctement synchronisé avec la vidéo
  useEffect(() => {
    if (videoRef.current) {
      // Synchroniser l'état avec l'état actuel de la vidéo
      setIsMuted(videoRef.current.muted);
      setIsPaused(videoRef.current.paused);
      setVolume(videoRef.current.volume);
    }
  }, []);

  const toggleSound = () => {
    if (videoRef.current) {
      if (isMuted) {
        // Si c'est mute, on unmute avec le volume actuel
        videoRef.current.muted = false;
        setIsMuted(false);
      } else {
        // Si c'est pas mute, on mute
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    }
  };

  const setVolumeLevel = (newVolume) => {
    if (videoRef.current) {
      const clampedVolume = Math.max(0, Math.min(1, newVolume));
      videoRef.current.volume = clampedVolume;
      setVolume(clampedVolume);
      
      // Si le volume est à 0, on mute automatiquement
      if (clampedVolume === 0) {
        videoRef.current.muted = true;
        setIsMuted(true);
      } else {
        // Si on augmente le volume et que c'était mute, on unmute
        if (isMuted) {
          videoRef.current.muted = false;
          setIsMuted(false);
        }
      }
    }
  };

  const togglePause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(console.error);
        setIsPaused(false);
      } else {
        videoRef.current.pause();
        setIsPaused(true);
      }
    }
  };

  const setVideoElement = (element) => {
    if (element && element !== videoRef.current) {
      // Nettoyer l'ancienne vidéo si elle existe
      if (videoRef.current) {
        videoRef.current.removeEventListener('play', handlePlay);
        videoRef.current.removeEventListener('pause', handlePause);
        videoRef.current.removeEventListener('volumechange', handleVolumeChange);
        // Pause l'ancienne vidéo pour éviter les conflits
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
      
      videoRef.current = element;
      
      // Appliquer l'état actuel à la nouvelle vidéo
      if (videoRef.current) {
        videoRef.current.muted = isMuted;
        videoRef.current.volume = volume; // Appliquer le volume par défaut
        
        // Ajouter les écouteurs d'événements
        videoRef.current.addEventListener('play', handlePlay);
        videoRef.current.addEventListener('pause', handlePause);
        videoRef.current.addEventListener('volumechange', handleVolumeChange);
        
        // Forcer la lecture si pas en pause
        if (!isPaused) {
          const playVideo = () => {
            if (videoRef.current && !videoRef.current.paused) {
              videoRef.current.play().catch(console.error);
            }
          };
          
          // Si la vidéo est déjà chargée, jouer immédiatement
          if (videoRef.current.readyState >= 2) {
            playVideo();
          } else {
            // Sinon, attendre que la vidéo soit chargée
            const handleLoadedData = () => {
              playVideo();
              videoRef.current.removeEventListener('loadeddata', handleLoadedData);
            };
            videoRef.current.addEventListener('loadeddata', handleLoadedData);
            
            // Fallback : essayer de jouer après un court délai
            setTimeout(() => {
              playVideo();
            }, 100);
          }
        }
      }
    }
  };

  const handlePlay = () => {
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleVolumeChange = () => {
    if (videoRef.current) {
      setIsMuted(videoRef.current.muted);
      setVolume(videoRef.current.volume);
    }
  };

  const forcePlay = () => {
    if (videoRef.current && !isPaused) {
      videoRef.current.play().catch(console.error);
    }
  };

  const cleanupAllVideos = () => {
    // Trouver toutes les vidéos dans le DOM et les pauser
    const allVideos = document.querySelectorAll('video');
    allVideos.forEach(video => {
      if (video !== videoRef.current) {
        video.pause();
        video.currentTime = 0;
        video.muted = true;
      }
    });
  };

  const syncVideoState = () => {
    if (videoRef.current) {
      // Synchroniser l'état avec la vidéo actuelle
      setIsMuted(videoRef.current.muted);
      setIsPaused(videoRef.current.paused);
    }
  };

  const value = {
    isMuted,
    isPaused,
    volume,
    toggleSound,
    setVolumeLevel,
    togglePause,
    setVideoElement,
    videoRef,
    forcePlay,
    cleanupAllVideos,
    syncVideoState
  };

  return (
    <VideoContext.Provider value={value}>
      {children}
    </VideoContext.Provider>
  );
};
